import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { navModule } from './modules/nav';
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  createApiRef,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { OAuth2 } from '@backstage/core-app-api';
import {
  SignInPageBlueprint,
  ThemeBlueprint,
  TranslationBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';
import { Progress, UserIdentity } from '@backstage/core-components';
import {
  createFrontendModule,
  ApiBlueprint,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { Navigate } from 'react-router-dom';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  genPageTheme,
  pageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import translationResources from './translations/resources';
import { gitLabGroupPickerField } from './scaffolderFields/GitLabGroupPicker';
import { gitLabClustersPickerField } from './scaffolderFields/GitLabClustersPicker';
import { namespacePickerField } from './scaffolderFields/NamespacePicker';
import { currentUserEmailPickerField } from './scaffolderFields/CurrentUserEmailPicker';
import { ingressHostPrefixPickerField } from './scaffolderFields/IngressHostPrefixPicker';
import { ibictEmailPrefixPickerField } from './scaffolderFields/IbictEmailPrefixPicker';
import { passwordPolicySecretField } from './scaffolderFields/PasswordPolicySecret';
import { ibictScaffolderModule } from './modules/scaffolder';

// API ref do provider OIDC do Keycloak (sso.suporte.ibict.br, realm ibict).
const oidcAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({ id: 'auth.oidc' });

// Cliente OAuth2 que conversa com o provider oidc do auth-backend.
const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'Keycloak Ibict',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

// SignInPage custom — chama oidcAuthApi.signIn() diretamente.
//
// Por que nao SignInPage do core-components com auto=true:
// O componente upstream chama getBackstageIdentity({ instantPopup: true })
// que IGNORA app.enableExperimentalRedirectFlow e SEMPRE abre popup —
// muitos navegadores bloqueiam por ser triggered automaticamente sem
// interacao do usuario.
//
// Aqui chamamos signIn() sem instantPopup: com a flag de redirect flow
// ativa no app-config, o DefaultAuthConnector faz window.location.href
// para /api/auth/oidc/start e o flow termina via redirect normal.
const IbictSignInPage = (props: SignInPageProps) => {
  const authApi = useApi(oidcAuthApiRef);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Tenta sessao existente primeiro (sem disparar popup/redirect).
        const existing = await authApi.getBackstageIdentity({ optional: true });
        if (existing && !cancelled) {
          props.onSignInSuccess(
            UserIdentity.create({ identity: existing.identity, authApi }),
          );
          return;
        }
        // Sem sessao — chama signIn(). Com enableExperimentalRedirectFlow=true
        // no app-config, o DefaultAuthConnector faz window.location redirect
        // em vez de abrir popup.
        await authApi.signIn();
        // Apos signIn() retornar (callback do redirect), busca identity.
        const fresh = await authApi.getBackstageIdentity({ optional: true });
        if (fresh && !cancelled) {
          props.onSignInSuccess(
            UserIdentity.create({ identity: fresh.identity, authApi }),
          );
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <h2>Falha ao autenticar</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return <Progress />;
};

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => <IbictSignInPage {...props} />,
  },
});

// Tema IBICT — paleta inspirada no GitLab dark:
//   • backgrounds em chumbo escuro (#1F1F1F page, #2B2B2B paper),
//   • sidebar com gradiente sutil de PANTONE 534C → quase preto,
//   • acento azul (#3F88E5) para links/destaque,
//   • marca PANTONE 534C (#2E406B) reservada para sidebar/headings.
//
// Os IDs 'light' e 'dark' propositadamente sobrescrevem os builtins do
// Backstage — sem virar opcao "IBICT" no toggle, viram OS temas.
const IBICT_BLUE = '#2E406B'; // PANTONE 534C — marca
const IBICT_BLUE_DEEP = '#1B2641'; // tom mais escuro pra base do gradiente
const IBICT_ACCENT = '#3F88E5'; // azul-info (botoes, links, indicator)

const sidebarGradient = `linear-gradient(180deg, ${IBICT_BLUE} 0%, ${IBICT_BLUE_DEEP} 60%, #121212 100%)`;
const ibictPageTheme = {
  ...pageTheme,
  framework: genPageTheme({
    colors: ['#D97706', '#EA580C'],
    shape: shapes.wave,
  }),
  resource: genPageTheme({
    colors: ['#0F766E', '#14B8A6'],
    shape: shapes.wave,
  }),
};

const ibictDarkUnified = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: IBICT_ACCENT },
    secondary: { main: IBICT_BLUE },
    background: {
      default: '#1F1F1F',
      paper: '#2B2B2B',
    },
    navigation: {
      ...palettes.dark.navigation,
      background: IBICT_BLUE_DEEP,
      indicator: IBICT_ACCENT,
      color: '#C9CDD4',
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: 'rgba(255,255,255,0.06)',
      },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: ibictPageTheme,
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          '&[href="/create/tasks"], &[href^="/create/tasks/"]': {
            display: 'none',
          },
          '&[data-testid="header-tab-1"]': {
            display: 'none',
          },
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: sidebarGradient,
          backgroundSize: 'cover',
        },
      },
    },
  },
});

const ibictLightUnified = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: IBICT_BLUE },
    secondary: { main: IBICT_ACCENT },
    navigation: {
      ...palettes.light.navigation,
      background: IBICT_BLUE_DEEP,
      indicator: IBICT_ACCENT,
      color: '#C9CDD4',
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: 'rgba(255,255,255,0.06)',
      },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: ibictPageTheme,
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          '&[href="/create/tasks"], &[href^="/create/tasks/"]': {
            display: 'none',
          },
          '&[data-testid="header-tab-1"]': {
            display: 'none',
          },
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: sidebarGradient,
          backgroundSize: 'cover',
        },
      },
    },
  },
});

const ibictLightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ibictLightUnified} children={children} />
      ),
    },
  },
});

const ibictDarkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ibictDarkUnified} children={children} />
      ),
    },
  },
});

// Translation extensions (uma por plugin) — geradas a partir de
// translations/resources.ts (autogerado por backstage-cli translations
// import). Idiomas disponiveis: en (default upstream) + pt (IBICT).
const translationExtensions = translationResources.map((resource, idx) =>
  TranslationBlueprint.make({
    name: `pt-${idx}`,
    params: { resource },
  }),
);

// Pagina home — redireciona / para /create. O app IBICT nao tem
// home propria; o ponto de entrada esperado e' criar projeto a partir
// de modelo. Sem essa extension, abrir o root `/` cai num 404 do
// roteador (nenhum plugin reivindica `/`).
const homePage = PageBlueprint.make({
  name: 'home',
  params: {
    path: '/',
    loader: async () => <Navigate to="/create" replace />,
  },
});

// Modulo frontend agregando OIDC API + SignInPage + traducoes + temas IBICT.
const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    oidcAuthApi,
    signInPage,
    homePage,
    ibictLightTheme,
    ibictDarkTheme,
    ...translationExtensions,
  ],
});

// Custom fields do Scaffolder (em pluginId: 'scaffolder').
const scaffolderFieldsModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    gitLabGroupPickerField,
    gitLabClustersPickerField,
    namespacePickerField,
    currentUserEmailPickerField,
    ingressHostPrefixPickerField,
    ibictEmailPrefixPickerField,
    passwordPolicySecretField,
  ],
});

export default createApp({
  features: [
    catalogPlugin,
    navModule,
    appModule,
    scaffolderFieldsModule,
    ibictScaffolderModule,
  ],
});
